import React, { useMemo, useState } from "react";
import "./sidebar.css";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@/components/ui-components/icon";
import type { Category } from "@/data/categories";
import type { InterpolationValues } from "@/i18n/types";
import type { LocaleCode } from "@/i18n/locales";
import { CategoryIcon } from "@/components/ui-components/category-icon/category-icon";

export function Sidebar({ categories, locale, t, children }: { categories: Category[]; locale: LocaleCode; t: (key: string, values?: InterpolationValues) => string; children?: React.ReactNode }) {
    const location = useLocation();
    const homePath = `/${locale}`;
    const offersPath = `/${locale}/special-offers`;
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => new Set());
    const categoryGroups = useMemo(() => {
        const primary = categories.filter(category => !category.breadcrumbs?.length || category.breadcrumbs[0]?.id === category.id);
        const primaryIds = new Set(primary.map(category => category.id));
        const childrenByParent = new Map<string, Category[]>();

        for (const category of categories) {
            if (primaryIds.has(category.id)) continue;
            const parentId = category.breadcrumbs?.at(-2)?.id;
            if (!parentId || !primaryIds.has(parentId)) continue;
            const currentChildren = childrenByParent.get(parentId) ?? [];
            currentChildren.push(category);
            childrenByParent.set(parentId, currentChildren);
        }

        return primary.map(category => {
            const nested = [...(category.children ?? []), ...(childrenByParent.get(category.id) ?? [])];
            return { category, children: [...new Map(nested.map(child => [child.id, child])).values()] };
        });
    }, [categories]);

    const expandCategory = (categoryId: string) => {
        setExpandedCategories(current => {
            const next = new Set(current);
            next.add(categoryId);
            return next;
        });
    };

    return (
        <aside className="sidebar">
            <div className="card">
                <div className="sidebar-head"><strong>{t("layout.sidebar.title")}</strong></div>
                <nav className="sidebar-nav">
                    <Link className={`nav-link${location.pathname === homePath ? " active" : ""}`} to={homePath}><span className="nav-icon"><Icon name="home" size="sm" /></span><span>{t("layout.sidebar.homePage")}</span></Link>
                    {categoryGroups.map(({ category, children: subcategories }) => {
                        const path = `/${locale}/products/${category.id}`;
                        const categoryName = category.name ?? (category.translationKey ? t(category.translationKey) : "");
                        const isExpanded = expandedCategories.has(category.id) || location.pathname === path || subcategories.some(subcategory => location.pathname === `/${locale}/products/${subcategory.id}`);
                        const subcategoryId = `sidebar-subcategories-${category.id}`;
                        return <div className="sidebar-category" key={category.id}>
                            <Link className={`nav-link${location.pathname === path ? " active" : ""}`} to={path} onClick={() => subcategories.length > 0 && expandCategory(category.id)}><span className="nav-icon"><CategoryIcon categoryId={category.id} /></span><span>{categoryName}</span><span className="nav-count">{category.count}</span>{subcategories.length > 0 && <span className={`nav-caret${isExpanded ? " is-expanded" : ""}`} aria-hidden="true"><Icon name="chevron-right" size="sm" /></span>}</Link>
                            {subcategories.length > 0 && isExpanded && <div className="sidebar-subcategories" id={subcategoryId}>
                                {subcategories.map(subcategory => {
                                    const subcategoryPath = `/${locale}/products/${subcategory.id}`;
                                    return <Link className={`sidebar-subcategory${location.pathname === subcategoryPath ? " active" : ""}`} to={subcategoryPath} key={subcategory.id}><span>{subcategory.name ?? (subcategory.translationKey ? t(subcategory.translationKey) : "")}</span><span className="nav-count">{subcategory.count}</span></Link>;
                                })}
                            </div>}
                        </div>;
                    })}
                    <div className="sidebar-divider" />
                    <Link className={`nav-link${location.pathname === offersPath ? " active" : ""}`} to={offersPath}><span className="nav-icon"><Icon name="tag" size="sm" /></span><span>{t("layout.sidebar.deals")}</span></Link>
                </nav>
            </div>
            {children}
        </aside>
    );
}
